import { Schemas } from "@kollectivia/shared";
/**
 * FormFillPage — remplissage conseiller d'un formulaire, puis bascule vers un
 * dossier (F2-Forms-1d-ii). Route `/formulaires/:id/versions/:versionId/remplir`.
 *
 * Réutilise : <FormRenderer mode="fill">, <ProjectPicker>, le contrat de
 * validation submit shared (via `_parts/fillData`), l'unsaved guard global, et
 * les hooks 1b-ii/1d-i (create/patch/submit-to-intake).
 *
 * Machine d'état réseau (§3-4 plan) — état local, `responseId` conservé dès le
 * 1er create réussi pour tout retry :
 *   - Save : create paresseux (1er) sinon PATCH (si dirty) ;
 *   - Submit : garantit un brouillon à jour (create OU PATCH-si-dirty) PUIS
 *     `submit-to-intake { project_id }` ; navigation `/intake-events/:id` au succès.
 *
 * Idempotence : rejeu même projet → 200 (aucun nouvel event) traité comme succès.
 * Fenêtre create-réseau-ambigu : single-flight + aucun retry auto + message
 * explicite ; risque résiduel accepté (dette P2 D-NEW-FORMS-1DII-CREATE-DUP-WINDOW).
 * `allowNavigation()` appelé UNIQUEMENT juste avant la navigation post-submit.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { FormRenderer, type RenderSchema } from "../../components/forms/FormRenderer";
import { ReferenceFieldPicker } from "../../components/forms/ReferenceFieldPicker";
import { ProjectPicker } from "../../components/ProjectPicker";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  useCreateFormResponse,
  useFormResponse,
  type FormResponseRow,
  usePatchFormResponse,
  useSubmitFormResponse,
  useSubmitFormResponseToIntake,
} from "../../lib/queries/formResponses";
import {
  type FormTemplateRow,
  type FormTemplateVersionRow,
  useFormTemplate,
  useFormTemplateVersions,
  useFormVersion,
} from "../../lib/queries/formTemplates";
import { useProject } from "../../lib/queries/projects";
import { useAttachQuoteRequestSpec, useQuoteRequest } from "../../lib/queries/quoteRequests";
import { construireRetour, estDestinationConnue } from "../../lib/retourParcours";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

import {
  type FieldIssue,
  messagesDIssues,
  toWireData,
  validateDraftData,
  validateSubmitData,
} from "./_parts/fillData";
import { formFillPath } from "./editorRoute";

/**
 * 9.BB.1 — le contexte de parcours, quand on remplit DEPUIS une demande.
 *
 * Le conseiller qui arrive ici depuis « préparer la demande » a déjà répondu à
 * la question du dossier : elle est portée par la demande dont il vient. Lui
 * redemander « dossier de destination » serait lui faire choisir une deuxième
 * fois, avec le risque de se tromper — et le faire passer par un
 * `intake_event` « à résoudre » lui demanderait de résoudre un rattachement
 * qui n'a jamais été ambigu.
 *
 * Le dossier ne vient PAS de l'URL : seul l'identifiant de la demande y voyage,
 * et le dossier est relu depuis cette demande. Un identifiant forgé ne donne
 * donc rien de plus que ce que le cabinet a déjà le droit de lire, et les deux
 * gardes serveur restent en vigueur — le dossier est vérifié tenant-scopé à la
 * création, et l'attach revérifie l'égalité des dossiers.
 */
type ContexteDemande = {
  /** La réponse devient-elle le cahier de charges de la demande ? */
  rattacheAuCahier: boolean;
  quoteRequestId: string;
  projectId: string;
  retour: string;
};

export function FormFillPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const [searchParams] = useSearchParams();
  const [createdResponseId, setCreatedResponseId] = useState<string | null>(null);

  const resumeId = searchParams.get("response_id");
  const validResumeId = resumeId !== null && Schemas.UuidSchema.safeParse(resumeId).success;
  const resume = useFormResponse(validResumeId ? resumeId : undefined);
  const destination = searchParams.get("retour");
  const retourId = searchParams.get("retour_id");
  // Deux destinations ramènent au même écran, et elles ne disent PAS la même
  // chose. `demande-preparer` signifie « ce que tu remplis EST le cahier de
  // charges » et déclenche le rattachement. `demande-preparer-retour` signifie
  // seulement « reviens ici » — le cas d'un formulaire simplement EXIGÉ par le
  // dossier, qui n'a aucune raison de devenir ce que l'assureur reçoit.
  const versPreparation =
    destination === "demande-preparer" || destination === "demande-preparer-retour";
  const cibleRetour =
    estDestinationConnue(destination) && versPreparation
      ? construireRetour({ destination, id: retourId })
      : null;
  // La demande n'est chargée que si le retour est réellement constructible :
  // une destination inconnue ou un identifiant malformé ne déclenche aucune
  // requête, et le remplissage retombe sur son comportement autonome.
  const demande = useQuoteRequest(cibleRetour && retourId ? retourId : undefined);
  const contexte: ContexteDemande | null =
    cibleRetour && retourId && demande.data?.project_id
      ? {
          quoteRequestId: retourId,
          projectId: demande.data.project_id,
          retour: cibleRetour,
          rattacheAuCahier: destination === "demande-preparer",
        }
      : null;

  const templateQ = useFormTemplate(id);
  const versionsQ = useFormTemplateVersions(id);
  const versionQ = useFormVersion(versionId);

  if (
    (templateQ.isLoading && !templateQ.data) ||
    (versionQ.isLoading && !versionQ.data) ||
    (versionsQ.isLoading && !versionsQ.data)
  ) {
    return <FillSkeleton />;
  }

  const notFound =
    (templateQ.error instanceof ApiException && templateQ.error.status === 404) ||
    (versionQ.error instanceof ApiException && versionQ.error.status === 404);

  if (notFound || templateQ.error || versionQ.error) {
    return (
      <FillErrorPanel
        message={notFound ? t("form_templates.fill.not_found") : t("errors.generic")}
        backTo={id ? `/formulaires/${id}` : "/formulaires"}
      />
    );
  }

  const template = templateQ.data?.template;
  const version = versionQ.data;
  if (!template || !version) return null;

  // Cohérence version ↔ template (anti-leak → 404).
  if (version.template_id !== template.id) {
    return (
      <FillErrorPanel
        message={t("form_templates.fill.not_found")}
        backTo={`/formulaires/${template.id}`}
      />
    );
  }

  if (template.status === "archived") {
    return (
      <FillErrorPanel
        message={t("form_templates.fill.archived")}
        backTo={`/formulaires/${template.id}`}
      />
    );
  }

  // Seules les versions publiées sont remplissables (brouillons exclus).
  if (version.published_at === null) {
    return (
      <FillErrorPanel
        message={t("form_templates.fill.not_published")}
        backTo={`/formulaires/${template.id}`}
      />
    );
  }

  if (
    Schemas.formPurpose(version.schema) === "mandate" ||
    Schemas.hasSignatureFields(version.schema)
  ) {
    return (
      <FillErrorPanel
        message={t("request_flow.client_signature_only")}
        backTo={cibleRetour ?? "/requests/new"}
      />
    );
  }
  if (contexte?.rattacheAuCahier && !Schemas.isBusinessSpec(version.schema)) {
    return (
      <FillErrorPanel
        message={t("request_flow.no_business_template")}
        backTo={cibleRetour ?? "/requests/new"}
      />
    );
  }

  const publishedVersions = (versionsQ.data?.items ?? [])
    .filter((v) => v.published_at !== null)
    .sort((a, b) => b.version_number - a.version_number);

  // Le contexte est encore en vol : monter <Fill> sans lui ferait apparaître le
  // sélecteur de dossier une fraction de seconde, puis disparaître.
  if (cibleRetour && demande.isPending) return <FillSkeleton />;

  if (resumeId !== null) {
    if (validResumeId && resume.isPending) return <FillSkeleton />;
    // Keep the loaded editor mounted if only a background read fails.
    // The revision precondition still protects the next write.
    const row = resume.data?.response;
    if (
      !validResumeId ||
      !row ||
      row.status !== "draft" ||
      !row.revision ||
      row.template_version_id !== version.id ||
      resume.data?.template.id !== template.id ||
      (cibleRetour !== null &&
        (!contexte || row.project_id !== contexte.projectId || demande.data?.status !== "draft"))
    ) {
      return (
        <FillErrorPanel
          message={t("form_templates.fill.resume_unavailable")}
          backTo={cibleRetour ?? "/reponses"}
        />
      );
    }
  }

  return (
    <Fill
      key={`${version.id}:${resumeId === createdResponseId ? "new" : (resumeId ?? "new")}`}
      onDraftCreated={setCreatedResponseId}
      initialResponse={resumeId ? resume.data?.response : undefined}
      template={template}
      version={version}
      publishedVersions={publishedVersions}
      lang={lang}
      contexte={contexte}
      recherche={searchParams.toString()}
    />
  );
}

function Fill(props: {
  onDraftCreated: (id: string) => void;
  initialResponse?: FormResponseRow;
  template: FormTemplateRow;
  version: FormTemplateVersionRow;
  publishedVersions: FormTemplateVersionRow[];
  lang: "fr" | "en";
  /** Non nul quand on remplit DEPUIS la préparation d'une demande. */
  contexte: ContexteDemande | null;
  /** La query string courante — conservée quand on change de version. */
  recherche: string;
}) {
  const {
    template,
    version,
    publishedVersions,
    lang,
    contexte,
    recherche,
    initialResponse,
    onDraftCreated,
  } = props;
  // Une exigence désigne UNE version. Changer de version dans ce parcours ne
  // pourrait pas la satisfaire.
  const versionImposee = !!initialResponse || (contexte !== null && !contexte.rattacheAuCahier);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = version.schema as unknown as RenderSchema;

  const [values, setValues] = useState<Record<string, unknown>>(() => initialResponse?.data ?? {});
  const [fieldErrors, setFieldErrors] = useState<Record<string, FieldIssue>>({});
  const [validationMode, setValidationMode] = useState<"draft" | "submit">("draft");
  // Le dossier vient du contexte quand il y en a un — et il n'est alors ni
  // modifiable ni redemandé.
  const [projectChoisi, setProjectChoisi] = useState<string | undefined>(undefined);
  const projectId = initialResponse?.project_id ?? contexte?.projectId ?? projectChoisi;
  // 9.AC.1 — l'entreprise du dossier choisi restreint la liste des contacts.
  // Proposer les contacts de tout le cabinet inviterait à rattacher le contact
  // d'un client à un autre.
  const projetChoisi = useProject(projectId);
  const [responseId, setResponseId] = useState<string | null>(initialResponse?.id ?? null);
  const [revision, setRevision] = useState(initialResponse?.revision);
  const [savedWire, setSavedWire] = useState<string>(() =>
    JSON.stringify(toWireData(initialResponse?.data ?? {}, version.schema)),
  );
  const [banner, setBanner] = useState<{ tone: "error" | "info"; message: string } | null>(null);

  const create = useCreateFormResponse();
  const patch = usePatchFormResponse();
  const submit = useSubmitFormResponseToIntake();
  const soumettre = useSubmitFormResponse();
  const rattacher = useAttachQuoteRequestSpec();
  const qc = useQueryClient();
  const busy =
    create.isPending ||
    patch.isPending ||
    submit.isPending ||
    soumettre.isPending ||
    rattacher.isPending;

  const wireData = useMemo(() => toWireData(values, version.schema), [values, version.schema]);
  const currentWire = useMemo(() => JSON.stringify(wireData), [wireData]);
  const dirty = currentWire !== savedWire;

  const { allowNavigation } = useUnsavedChangesGuard(
    dirty,
    t("form_templates.fill.unsaved_message"),
    "response_id",
  );

  const onSave = () => {
    if (!dirty || busy) return;
    setBanner(null);
    const wire = toWireData(values, version.schema);
    setValidationMode("draft");
    const issues = validateDraftData(wire, version.schema);
    setFieldErrors(issues);
    if (Object.keys(issues).length > 0) {
      setBanner({ tone: "error", message: "form_templates.fill.fix_errors" });
      return;
    }
    if (responseId === null) {
      create.mutate(
        {
          template_version_id: version.id,
          data: wire,
          ...(projectId ? { project_id: projectId } : {}),
        },
        {
          onSuccess: (row) => {
            setResponseId(row.id);
            setRevision(row.revision);
            setSavedWire(JSON.stringify(wire));
            // Promote the URL to the persisted identity without remounting the
            // editor: input entered while the save was in flight must survive.
            qc.setQueryData(["form-responses", "detail", row.id], {
              response: row,
              template,
              version,
            });
            onDraftCreated(row.id);
            const params = new URLSearchParams(recherche);
            params.set("response_id", row.id);
            allowNavigation();
            navigate({ search: params.toString() }, { replace: true });
            toast.success(t("form_templates.fill.draft_saved"));
          },
          onError: (err) => setBanner(saveErrorBanner(err, lang, t)),
        },
      );
    } else {
      patch.mutate(
        { id: responseId, data: wire, ...(revision ? { expected_revision: revision } : {}) },
        {
          onSuccess: (row) => {
            setRevision(row.revision);
            setSavedWire(JSON.stringify(wire));
            toast.success(t("form_templates.fill.draft_saved"));
          },
          onError: (err) => setBanner(saveErrorBanner(err, lang, t)),
        },
      );
    }
  };

  const onSubmit = async () => {
    if (busy) return;
    if (!projectId) {
      setBanner({ tone: "error", message: t("form_templates.fill.project_required") });
      return;
    }
    const wire = toWireData(values, version.schema);
    setValidationMode("submit");
    const errs = validateSubmitData(wire, version.schema);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setBanner({ tone: "error", message: "form_templates.fill.fix_errors" });
      return;
    }
    setFieldErrors({});
    setBanner(null);

    try {
      // Garantit un brouillon à jour SANS requête redondante : create (1er) ou
      // PATCH (si dirty) ; `responseId` posé immédiatement pour tout retry.
      let rid = responseId;
      let currentRevision = revision;
      if (rid === null) {
        const row = await create.mutateAsync({
          template_version_id: version.id,
          data: wire,
          ...(projectId ? { project_id: projectId } : {}),
        });
        rid = row.id;
        currentRevision = row.revision;
        setResponseId(row.id);
        setRevision(row.revision);
        setSavedWire(JSON.stringify(wire));
      } else if (dirty) {
        const row = await patch.mutateAsync({
          id: rid,
          data: wire,
          ...(currentRevision ? { expected_revision: currentRevision } : {}),
        });
        currentRevision = row.revision;
        setRevision(row.revision);
        setSavedWire(JSON.stringify(wire));
      }
      // 9.BB.1 — deux fins, et l'origine décide laquelle.
      //
      // Depuis la préparation d'une demande, le dossier est connu : on soumet, on
      // rattache, on revient. Le `submit-to-intake` reste la fin de l'autre
      // parcours — celui où l'on remplit un formulaire SANS savoir encore à quel
      // dossier il appartient, et où l'`intake_event` porte précisément cette
      // question ouverte. Rien n'est retiré ; une seconde fin est ajoutée.
      if (contexte) {
        await soumettre.mutateAsync({
          id: rid,
          ...(currentRevision ? { expected_revision: currentRevision } : {}),
        });
        // Un formulaire simplement EXIGÉ n'est pas le cahier de charges. Le
        // rattacher ferait de lui ce que l'assureur reçoit — et la pièce jointe
        // de l'envoi, rendue en PDF avec toutes ses valeurs. Relevé par la revue
        // Codex de la PR #146.
        if (contexte.rattacheAuCahier) {
          await rattacher.mutateAsync({
            quoteRequestId: contexte.quoteRequestId,
            formResponseId: rid,
          });
        }
        // Le serveur a bougé ; les deux écrans de retour ne le savent pas.
        //
        // `staleTime` vaut 30 s et `refetchOnWindowFocus` est désactivé : un
        // conseiller qui remplit un formulaire court et revient dans la minute
        // retrouve un cache FRAIS au sens de React Query — et faux. Il verrait
        // « À faire » sur ce qu'il vient de terminer, et l'envoi encore bloqué,
        // sans rien à corriger nulle part.
        //
        // La réconciliation serveur ajoutée dans la même PR rend l'état PERSISTÉ
        // juste ; elle ne dit rien au navigateur. Relevé par la revue Codex.
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["projects", contexte.projectId, "completeness"] }),
          qc.invalidateQueries({
            queryKey: ["quote-requests", "readiness", contexte.quoteRequestId],
          }),
        ]);
        allowNavigation();
        navigate(contexte.retour);
        return;
      }
      const res = await submit.mutateAsync({
        id: rid,
        project_id: projectId,
        ...(currentRevision ? { expected_revision: currentRevision } : {}),
      });
      allowNavigation();
      navigate(`/intake-events/${res.intake_event_id}`);
    } catch (err) {
      handleSubmitError(err, lang, t, { setBanner, setFieldErrors, version, values });
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <nav className="mb-4 text-sm">
        {/* Sortir doit ramener d'où l'on vient, pas dans l'administration des
            modèles : le conseiller n'y allait pas. */}
        <Link
          to={contexte ? contexte.retour : `/formulaires/${template.id}`}
          className="hover:text-brand-700 focus-visible:ring-brand-500 rounded-sm text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          ←{" "}
          {contexte
            ? t("form_templates.fill.back_to_request")
            : t("form_templates.fill.back_to_template")}
        </Link>
      </nav>

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t("form_templates.fill.title", { name: template.name })}
        </h1>
        <p className="text-sm text-slate-500">
          {t("form_templates.fill.version_label", { number: version.version_number })}
        </p>
      </header>

      {/* Le sélecteur de version, et les deux raisons de ne pas le montrer tel quel.
          1. Quand une EXIGENCE impose une version, en choisir une autre ne peut
             pas la satisfaire : `requirement_key` désigne cette version-là. Le
             conseiller remplirait un formulaire pour rien, et l'exigence
             resterait ouverte sans qu'il comprenne pourquoi.
          2. Même dans le parcours du cahier de charges, `formFillPath` rend un
             chemin NU : naviguer avec lui perdait `retour` et `retour_id`, donc
             le contexte, donc le retour — et la page retombait sur le parcours
             par l'intake. Les paramètres sont conservés.
          Relevé par la revue Codex de la PR #146. */}
      {publishedVersions.length > 1 && !versionImposee && (
        <div className="mt-4">
          <label htmlFor="fill-version-select" className="block text-sm font-medium text-slate-700">
            {t("form_templates.fill.version_select_label")}
          </label>
          <select
            id="fill-version-select"
            value={version.id}
            onChange={(e) =>
              navigate({
                pathname: formFillPath(template.id, e.target.value),
                search: recherche,
              })
            }
            className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2 sm:w-auto"
          >
            {publishedVersions.map((v) => (
              <option key={v.id} value={v.id}>
                {t("form_templates.fill.version_option", { number: v.version_number })}
              </option>
            ))}
          </select>
        </div>
      )}

      {banner && (
        <div
          role={banner.tone === "error" ? "alert" : "status"}
          className={
            banner.tone === "error"
              ? "mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              : "border-brand-200 bg-brand-50 text-brand-700 mt-4 rounded-lg border px-4 py-3 text-sm"
          }
        >
          {banner.message === "form_templates.fill.fix_errors" ? t(banner.message) : banner.message}
        </div>
      )}

      <div className="mt-6">
        <FormRenderer
          schema={schema}
          locale={lang}
          mode="fill"
          values={values}
          onValuesChange={(next) => {
            setValues(next);
            if (Object.keys(fieldErrors).length > 0) {
              const validate = validationMode === "draft" ? validateDraftData : validateSubmitData;
              const issues = validate(toWireData(next, version.schema), version.schema);
              setFieldErrors(issues);
              if (
                Object.keys(issues).length === 0 &&
                banner?.message === "form_templates.fill.fix_errors"
              ) {
                setBanner(null);
              }
            }
          }}
          errors={messagesDIssues(fieldErrors, t)}
          // 9.AC.1 — les champs de référence deviennent saisissables ICI, sur le
          // parcours conseiller authentifié uniquement. Le parcours public ne
          // passe pas cette prop, et ne peut pas la passer par accident : lister
          // les entreprises du cabinet à un visiteur anonyme reviendrait à lui
          // offrir le portefeuille client.
          renderReferenceField={({ fieldKey, type, value, onChange, inputId, describedBy }) => (
            <ReferenceFieldPicker
              key={fieldKey}
              type={type}
              value={value}
              onChange={onChange}
              inputId={inputId}
              describedBy={describedBy}
              companyId={projetChoisi.data?.company_id ?? null}
            />
          )}
        />
      </div>

      {/* Rattachement au dossier. Une question — sauf quand elle est déjà répondue. */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 id="fill-project-label" className="text-base font-semibold text-slate-900">
          {t("form_templates.fill.project_section_title")}
        </h2>
        {contexte ? (
          <p className="mt-1 text-sm text-slate-600">
            {contexteTexte(t, contexte, projetChoisi.data?.title)}
          </p>
        ) : (
          <>
            <p className="mt-1 text-sm text-slate-500">
              {t("form_templates.fill.project_section_help")}
            </p>
            <div className="mt-3">
              <ProjectPicker
                value={projectId}
                onChange={setProjectChoisi}
                id="fill-project"
                disabled={busy || !!initialResponse?.project_id}
                ariaLabelledBy="fill-project-label"
              />
            </div>
          </>
        )}
      </section>

      {/* Barre d'actions. Single-flight : boutons désactivés pendant toute requête. */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        {!projectId && (
          <p className="text-xs text-slate-500 sm:mr-auto">
            {t("form_templates.fill.project_required_hint")}
          </p>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || busy}
          className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          {t("form_templates.fill.save_draft")}
        </button>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={!projectId || busy}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          {submit.isPending || soumettre.isPending || rattacher.isPending
            ? t("form_templates.fill.submitting")
            : contexte
              ? contexte.rattacheAuCahier
                ? t("form_templates.fill.submit_to_request")
                : t("form_templates.fill.submit_to_request_return")
              : t("form_templates.fill.submit_to_intake")}
        </button>
      </div>
    </div>
  );
}

type TFunc = ReturnType<typeof useTranslation>["t"];

/**
 * Ce que l'écran PROMET au conseiller, selon ce qu'il va réellement faire.
 *
 * Les deux contextes ramènent à la même demande, et c'est exactement pour ça
 * qu'il faut les distinguer ici : l'un fait de cette réponse le cahier de
 * charges — donc ce que l'assureur recevra —, l'autre l'enregistre seulement
 * dans le dossier pour satisfaire une exigence. Un libellé unique promettrait
 * un rattachement qui n'aura pas lieu, et l'écran mentirait sur ses propres
 * conséquences.
 */
function contexteTexte(t: TFunc, contexte: ContexteDemande, titre: string | undefined): string {
  if (contexte.rattacheAuCahier) {
    return titre
      ? t("form_templates.fill.project_from_request", { title: titre })
      : t("form_templates.fill.project_from_request_generic");
  }
  return titre
    ? t("form_templates.fill.project_from_request_requirement", { title: titre })
    : t("form_templates.fill.project_from_request_requirement_generic");
}

/** Bannière d'erreur de Save : réseau ambigu = message explicite (aucun retry auto). */
function saveErrorBanner(
  err: unknown,
  lang: "fr" | "en",
  t: TFunc,
): { tone: "error"; message: string } {
  if (err instanceof ApiException) {
    return { tone: "error", message: getApiErrorMessage(err, lang) };
  }
  // Pas de statut HTTP = réseau : le brouillon a peut-être été créé côté serveur.
  return { tone: "error", message: t("form_templates.fill.network_ambiguous") };
}

function handleSubmitError(
  err: unknown,
  lang: "fr" | "en",
  t: TFunc,
  ctx: {
    setBanner: (b: { tone: "error" | "info"; message: string } | null) => void;
    setFieldErrors: (e: Record<string, FieldIssue>) => void;
    version: FormTemplateVersionRow;
    values: Record<string, unknown>;
  },
) {
  if (err instanceof ApiException) {
    // 422 : le client a déjà validé avant submit → un 422 serveur est un edge
    // (course de version). Ré-affiche les erreurs par champ + message registre.
    if (err.status === 422 && err.apiError.code === "FORM_RESPONSE_INVALID_DATA") {
      const errs = validateSubmitData(
        toWireData(ctx.values, ctx.version.schema),
        ctx.version.schema,
      );
      if (Object.keys(errs).length > 0) ctx.setFieldErrors(errs);
    }
    // Tous les codes registre (404/409/422) → message bilingue de la source de
    // vérité (mismatch projet, archivé, non publiée, projet introuvable…).
    ctx.setBanner({ tone: "error", message: getApiErrorMessage(err, lang) });
    return;
  }
  // Pas de statut = réseau ambigu.
  ctx.setBanner({ tone: "error", message: t("form_templates.fill.network_ambiguous") });
}

function FillSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-6" role="status" aria-live="polite">
      <span className="sr-only">{t("app.loading")}</span>
      <div className="h-8 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="mt-6 space-y-4">
        <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
      </div>
    </div>
  );
}

function FillErrorPanel({ message, backTo }: { message: string; backTo: string }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div
        role="alert"
        className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center shadow-sm"
      >
        <p className="text-sm text-slate-700">{message}</p>
        <Link
          to={backTo}
          className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 mt-3 inline-block rounded-sm text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("form_templates.fill.back_to_template")}
        </Link>
      </div>
    </div>
  );
}
